import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
} from "@mui/material";
import { DataGrid, GridColumnMenu } from "@mui/x-data-grid";
import styled from "@emotion/styled";

import Modal from "Components/Common/Modal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import { billingOptionsForDispatchBills, getOptionLabel } from "common/enums";
import { powerAdmin4Roles } from "common/constant";
import CommentInputForReason from "Components/Common/CommentInputForReason";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getStatusForApproval, isAuthorizedUser } from "utils";

import {
  useLazyGetBackupPowerQuery,
  usePostDispatchBackupPowerMutation,
  usePutUpdateBackupPowerStatusMutation,
} from "apis/BackupPower";

export const StyledGrid = styled(DataGrid)`
  .MuiTablePagination-selectLabel,
  .MuiTablePagination-displayedRows {
    margin: 0;
  }
`;

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const BackupPowerReviewModal = ({ handleClose, t, isDispatchModal }) => {
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [isRevokedCommentModalVisible, setIsRevokedCommentModalVisible] =
    useState(false);
  const [isApproveBtnClicked, setIsApproveBtnClicked] = useState(false);

  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState(false);
  const [{ status }, setQuery] = useState({
    status: 2,
  });
  const [{ isSMS, isEmail }, setCommunicationMode] = useState({
    isSMS: false,
    isEmail: false,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [updateBackupPowerStatus, { isLoading: isBackupPowerBillsUpdating }] =
    usePutUpdateBackupPowerStatusMutation();

  const { roles } = useSelector((state) => state.Login);

  const [searchParams] = useSearchParams();
  const billingCycleId = searchParams.get("billingCycleId");

  const [
    getBackupPowerData,
    { data: { data: backupPowerData } = {}, isLoading },
  ] = useLazyGetBackupPowerQuery();
  const [dispatchBackupPower, { isLoading: isBackupPowerBillsDispatching }] =
    usePostDispatchBackupPowerMutation();

  const backupPowerBills = useMemo(
    () =>
      isDispatchModal
        ? backupPowerData ?? []
        : backupPowerData?.filter(
            ({ approvedBy, revokedBy }) =>
              getStatusForApproval({ approvedBy, revokedBy }, roles).canApprove
          ) ?? [],
    [backupPowerData, isDispatchModal]
  );

  const columns = useMemo(
    () => [
      {
        field: "consumerNumber",
        headerName: t("Consumer Number"),
        width: 190,
      },
      {
        field: "billNumber",
        headerName: t("Bill Number"),
        width: 190,
      },
      {
        field: "paymentDate",
        headerName: t("Payment Date"),
        renderCell: ({ row: { paymentDueDate } }) =>
          paymentDueDate
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "--",
        width: 190,
      },
      {
        field: "currentMonthBill",
        headerName: t("Current Month Bill"),
        renderCell: ({ row: { currentMonthBill } }) => (
          <span className="text-nowrap"> &#8377; {currentMonthBill}</span>
        ),
        width: 190,
      },
      {
        field: "pendingDues",
        headerName: t("Pending Dues"),
        renderCell: ({ row: { pendingDues } }) => (
          <span className="text-nowrap"> &#8377; {pendingDues}</span>
        ),
        width: 190,
      },

      {
        field: "status",
        headerName: t("Status"),
        renderCell: ({ row: { status } }) =>
          getOptionLabel("billCalculationStatus", status),
        width: 190,
      },
    ],
    [backupPowerBills]
  );

  const canBackupPowerCalculationRevoked = useMemo(
    () =>
      !backupPowerBills.some(
        ({ approvedBy, revokedBy }) =>
          getStatusForApproval({ approvedBy, revokedBy }, roles).canRevoke
      ),
    [backupPowerBills]
  );

  const isAllBackupPowerCalculationsApproved = useMemo(
    () =>
      !backupPowerBills.some(
        ({ approvedBy, revokedBy }) =>
          getStatusForApproval({ approvedBy, revokedBy }, roles).canApprove
      ),
    [backupPowerBills]
  );

  const updatedOptions = useMemo(
    () => [{ label: "All", value: "" }, ...billingOptionsForDispatchBills],
    [billingOptionsForDispatchBills]
  );

  const submitBtnHandler = async () => {
    if (!isEmail && !isSMS) {
      toast.error("Select notification mode");
    } else {
      try {
        await dispatchBackupPower(billingCycleId, {
          notifyModes:
            isEmail && isSMS ? [1, 2] : isEmail ? [1] : isSMS ? [2] : "",
          billCalculationIds: selectedRowIds,
        });
        handleClose();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const revokeBtnHandler = async (comment) => {
    try {
      await updateBackupPowerStatus(
        {
          backupPowerBillCalculationIds: selectedRowIds,
          isApproved: false,
          comment,
        },
        "Backup power bills revoked successfully"
      );
    } catch (error) {
      console.error(error);
    }
  };

  const approveBtnHandler = async () => {
    try {
      await updateBackupPowerStatus(
        {
          backupPowerBillCalculationIds: selectedRowIds,
          isApproved: true,
        },
        "Backup power bills approved successfully"
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (billingCycleId) {
      (async () => {
        await getBackupPowerData(billingCycleId, {
          calculationStatus: isDispatchModal ? status : "",
        });
      })();
    }
  }, [billingCycleId, status]);

  return (
    <>
      <Backdrop open={isLoading} />

      <CommentInputForReason
        handleClose={() => {
          setIsRevokedCommentModalVisible(false);
        }}
        handleSubmit={(comment) => {
          revokeBtnHandler(comment);
          setIsRevokedCommentModalVisible(false);
        }}
        isOpen={isRevokedCommentModalVisible}
        isLoading={isBackupPowerBillsUpdating}
        title="Add Reason for Revoked"
        errorMessage="Please enter reason for revoked"
      />

      <ConfirmationDialog
        message="Are you sure you want to Approve selected Backup power bills?"
        onCancel={() => setIsApproveBtnClicked(false)}
        onConfirm={async () => {
          await approveBtnHandler();
          setIsApproveBtnClicked(false);
        }}
        isOpen={isApproveBtnClicked}
        isLoading={isBackupPowerBillsUpdating}
        title="Warning"
        variant="warning"
      />

      <ConfirmationDialog
        message="Are you sure you want to dispatch selected Backup Power bills?"
        onCancel={() => setIsSubmitBtnClicked(false)}
        onConfirm={async () => {
          await submitBtnHandler();
          setIsSubmitBtnClicked(false);
        }}
        isOpen={isSubmitBtnClicked}
        isLoading={isBackupPowerBillsDispatching}
        title="Warning"
        variant="warning"
      />

      <Modal
        isOpen
        handleClose={handleClose}
        title={isDispatchModal ? "Dispatch Bills" : "Review Bills"}
        maxWidth="lg"
      >
        <>
          {isDispatchModal && (
            <Grid container columnSpacing={2} className="mt-2 mb-2">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectField
                  className="w-100 mt-1"
                  size="small"
                  label="Status"
                  options={updatedOptions}
                  value={updatedOptions.find(({ value }) => value === status)}
                  onChange={(_, { value }) =>
                    setQuery((prev) => ({ ...prev, status: value }))
                  }
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2} lg={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isEmail}
                      onChange={() =>
                        setCommunicationMode((prev) => ({
                          ...prev,
                          isEmail: !prev.isEmail,
                        }))
                      }
                    />
                  }
                  label={t("Email")}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2} lg={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isSMS}
                      onChange={() =>
                        setCommunicationMode((prev) => ({
                          ...prev,
                          isSMS: !prev.isSMS,
                        }))
                      }
                    />
                  }
                  label={t("SMS")}
                />
              </Grid>
            </Grid>
          )}
          <StyledGrid
            checkboxSelection
            autoHeight
            disableRowSelectionOnClick
            rows={backupPowerBills ?? []}
            columns={columns}
            pageSizeOptions={[5, 10]}
            rowCount={backupPowerBills?.length}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onRowSelectionModelChange={(ids) => {
              // eslint-disable-next-line no-undef
              const selectedIDs = new Set(ids);
              setIsRowSelected(!!selectedIDs.size);
              const selectedRows = backupPowerBills
                .filter((row) => selectedIDs.has(row.id))
                .map(({ id }) => id);
              setSelectedRowIds(selectedRows);
            }}
            slots={{
              columnMenu: CustomColumnMenu,
              noRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  {t("No records found")}.
                </Stack>
              ),
            }}
            rowSelection
            keepNonExistentRowsSelected
          />
          <div className="text-end">
            {isDispatchModal ? (
              <Button
                variant="contained"
                className="mt-2"
                disabled={isLoading || !isRowSelected}
                onClick={() => setIsSubmitBtnClicked(true)}
              >
                {t("Submit")} {isLoading && <CircularProgress size={20} />}
              </Button>
            ) : (
              <>
                {!isAuthorizedUser(roles, powerAdmin4Roles) && (
                  <Button
                    variant="contained"
                    className="mt-2 me-2"
                    color="primary"
                    disabled={
                      canBackupPowerCalculationRevoked || !isRowSelected
                    }
                    onClick={() => setIsRevokedCommentModalVisible(true)}
                  >
                    {t("Revoke")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  className="mt-2 "
                  color="success"
                  disabled={
                    isAllBackupPowerCalculationsApproved || !isRowSelected
                  }
                  onClick={() => setIsApproveBtnClicked(true)}
                >
                  {t("Approve")}
                </Button>
              </>
            )}
          </div>
        </>
      </Modal>
    </>
  );
};

BackupPowerReviewModal.propTypes = {
  handleClose: PropTypes.func,
  isDispatchModal: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(BackupPowerReviewModal);
